import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFieldArray, UseFormReturn, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { ImageUpload } from 'src/features/systems/components/ImageUpload';
import { ALIGN_TRANSLATION_MAP, TICKET_PRINTER_TYPE_TRANSLATION_MAP } from 'src/features/systems/utils/constants';
import { SystemComponentFormSchemaValues } from 'src/features/systems/utils/formSchema';
import { systemsMessages } from 'src/features/systems/utils/messages';
import { ALIGN, TICKET_PRINTER_ROW_TYPE } from 'src/features/systems/utils/types';
import InputFormField from 'src/registry/components/forms/InputFormField';
import { Button } from 'src/registry/ui/Button';
import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { Separator } from 'src/registry/ui/Separator';
import { ICON_MAP } from 'src/utils/constants';

type TicketPrinterFormFieldsProps = {
    form: UseFormReturn<SystemComponentFormSchemaValues>;
    logoTitle?: string;
};

const TicketPrinterFormFields = ({ form, logoTitle }: TicketPrinterFormFieldsProps) => {
    const { formatMessage } = useIntl();

    const { fields, append, remove } = useFieldArray({
        control: form.control,
        name: 'data.payload',
    });

    const payload = useWatch({
        control: form.control,
        name: 'data.payload',
    });

    return (
        <>
            <InputFormField<SystemComponentFormSchemaValues>
                form={form}
                name="data.channel"
                type="number"
                label={formatMessage(systemsMessages.channel)}
                min={0}
            />
            <InputFormField<SystemComponentFormSchemaValues>
                form={form}
                name="data.topic_code"
                required={false}
                label={formatMessage(systemsMessages.topicCode)}
            />

            <FormField
                control={form.control}
                name="logo"
                render={({ field }) => {
                    const photoValue = form.watch('logo');
                    const logoTitleToDisplay = logoTitle ?? '.bmp';
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">{formatMessage(systemsMessages.logo)}</FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <ImageUpload
                                        onChange={field.onChange}
                                        imagePreview={
                                            typeof photoValue === 'string'
                                                ? photoValue
                                                : photoValue instanceof File
                                                  ? URL.createObjectURL(photoValue)
                                                  : logoTitleToDisplay
                                        }
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />

            <Separator className="mt-2 mb-4" />

            <div className="flex flex-1 flex-col gap-4">
                {fields?.map((field, index) => (
                    <div className="flex flex-1 justify-start gap-4" key={field.id}>
                        <FormLabel className="flex gap-0">
                            {formatMessage(systemsMessages.row, { number: index + 1 })}
                            <div className="text-primary-700">*</div>
                        </FormLabel>
                        <div className="flex w-full flex-col gap-2">
                            <div className="flex w-full justify-end gap-2">
                                <FormField
                                    control={form.control}
                                    name={`data.payload.${index}.align`}
                                    render={({ field }) => {
                                        return (
                                            <FormItem>
                                                <FormControlStack>
                                                    <FormControl>
                                                        <Select onValueChange={field.onChange} value={field.value}>
                                                            <SelectTrigger className="w-full truncate text-left">
                                                                <SelectValue
                                                                    placeholder={formatMessage(systemsMessages.align)}
                                                                />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {Object.entries(ALIGN_TRANSLATION_MAP).map(
                                                                    ([key, value]) => (
                                                                        <SelectItem key={key} value={key}>
                                                                            {formatMessage(value)}
                                                                        </SelectItem>
                                                                    ),
                                                                )}
                                                            </SelectContent>
                                                        </Select>
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormControlStack>
                                            </FormItem>
                                        );
                                    }}
                                />

                                <FormField
                                    control={form.control}
                                    name={`data.payload.${index}.type`}
                                    render={({ field }) => {
                                        return (
                                            <FormItem>
                                                <FormControlStack>
                                                    <FormControl>
                                                        <Select onValueChange={field.onChange} value={field.value}>
                                                            <SelectTrigger className="w-full">
                                                                <SelectValue
                                                                    placeholder={formatMessage(systemsMessages.type)}
                                                                />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {Object.entries(
                                                                    TICKET_PRINTER_TYPE_TRANSLATION_MAP,
                                                                ).map(([key, value]) => (
                                                                    <SelectItem key={key} value={key}>
                                                                        {formatMessage(value)}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormControlStack>
                                            </FormItem>
                                        );
                                    }}
                                />
                                <Button
                                    onClick={() => {
                                        remove(index);
                                    }}
                                    variant="destructiveLight"
                                    size="icon"
                                    className="shrink-0"
                                    type="button"
                                >
                                    <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                </Button>
                            </div>
                            {payload?.[index]?.type === TICKET_PRINTER_ROW_TYPE.TEXT && (
                                <FormField
                                    control={form.control}
                                    name={`data.payload.${index}.text`}
                                    render={({ field }) => (
                                        <FormItem className="flex w-full flex-1">
                                            <FormControlStack className="mr-13 flex w-full flex-1">
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            field.onChange(value);
                                                        }}
                                                        value={field.value}
                                                        placeholder={`${formatMessage(systemsMessages.text)}...`}
                                                        className="w-full"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormControlStack>
                                        </FormItem>
                                    )}
                                />
                            )}
                        </div>
                    </div>
                ))}

                <div className="flex">
                    <div className="md:w-36" />
                    <Button
                        variant="outline"
                        type="button"
                        className="flex-1"
                        onClick={() => {
                            append({
                                align: undefined as unknown as ALIGN,
                                type: undefined as unknown as TICKET_PRINTER_ROW_TYPE,
                                text: '',
                            });
                        }}
                    >
                        <FontAwesomeIcon className="text-primary-700" icon={ICON_MAP.ADD} />
                        {formatMessage(systemsMessages.addRowOnTicket)}
                    </Button>
                </div>
            </div>
        </>
    );
};

export default TicketPrinterFormFields;
